import React, { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

function createDebtPaymentSchema(balance, copy, formatAmount) {
  return z.object({
    amount: z.preprocess(
      (value) => {
        const text = String(value ?? "").trim();
        return text ? Number(text) : undefined;
      },
      z.number({
        required_error: copy.validation.amountRequired,
        invalid_type_error: copy.validation.amountValid,
      })
        .positive(copy.validation.amountPositive)
        .refine((value) => value <= balance, copy.validation.amountCannotExceed(formatAmount(balance)))
    ),
  });
}

function DebtPaymentDialog({ debt, copy, formatAmount, isRecording, onClose, onSubmit }) {
  const balance = useMemo(() => (
    debt ? Math.max(0, debt.amountOwed - debt.amountPaid) : 0
  ), [debt]);

  const form = useForm({
    resolver: zodResolver(createDebtPaymentSchema(balance, copy, formatAmount)),
    defaultValues: { amount: "" },
    mode: "onChange",
  });

  useEffect(() => {
    form.reset({ amount: "" });
  }, [debt?.id, form]);

  return (
    <Dialog open={!!debt} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.dialogTitle}</DialogTitle>
          <DialogDescription>
            {debt ? copy.dialogDescription(debt.customerName, formatAmount(balance)) : ""}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(({ amount }) => onSubmit(amount))}
            className="space-y-2"
          >
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                    {copy.amountLabel}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      data-testid="debt-payment-input"
                      autoFocus
                      placeholder="0"
                      inputMode="numeric"
                      onChange={(event) => field.onChange(event.target.value.replace(/[^0-9]/g, ""))}
                      className="font-mono tabular-nums h-11 text-lg focus-visible:ring-0 focus-visible:ring-transparent"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>
                {copy.cancel}
              </Button>
              <Button
                type="submit"
                data-testid="debt-payment-confirm"
                disabled={isRecording || !form.formState.isValid}
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> {isRecording ? copy.recording : copy.confirmPayment}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export { DebtPaymentDialog };
